// ABOUTME: Reusable video player component for educational helper videos
// ABOUTME: Material UI themed with responsive design, streaming support, and accessibility features

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PlayArrow } from '@mui/icons-material';

interface VideoPlayerProps {
  videoFile: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoFile }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!videoFile) {
    throw new Error('VideoPlayer requires a videoFile prop');
  }

  const videoSrc = `/videos/${videoFile}`;

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.load();
    }
  }, [videoFile]);

  const handlePlayClick = async () => {
    const video = videoRef.current;
    if (video) {
      try {
        await video.play();
        setIsPlaying(true);
        setShowControls(true);
      } catch (error) {
        console.error('Failed to play video:', error);
        setHasError(true);
      }
    }
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
    setShowControls(true);
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    setShowControls(false);
  };

  const handleLoadStart = () => {
    setIsLoading(true);
    setHasError(false);
  };

  const handleLoadedData = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleRetry = () => {
    const video = videoRef.current;
    if (video) {
      setHasError(false);
      setIsLoading(true);
      video.load();
    }
  };

  if (hasError) {
    return (
      <Box
        data-testid="video-player-container"
        sx={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '600px',
          mx: 'auto',
          mb: 3,
        }}
      >
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          Unable to load video. Please check your connection and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      data-testid="video-player-container"
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: isMobile ? '100%' : '600px',
        mx: 'auto',
        mb: 3,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 2,
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        src={videoSrc}
        controls={showControls}
        sx={{
          width: '100%',
          height: 'auto',
          display: 'block',
        }}
        onPlay={handleVideoPlay}
        onPause={handleVideoPause}
        onEnded={handleVideoEnded}
        onLoadStart={handleLoadStart}
        onLoadedData={handleLoadedData}
        onError={handleError}
        role="application"
        aria-label="Video player"
        tabIndex={0}
        controlsList="nodownload"
      />

      {/* Play Button Overlay */}
      {!isPlaying && !isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.common.black, 0.4),
            cursor: 'pointer',
          }}
          onClick={handlePlayClick}
        >
          <IconButton
            color="primary"
            size="large"
            onClick={handlePlayClick}
            aria-label="Play video"
            sx={{
              backgroundColor: alpha(theme.palette.common.white, 0.9),
              '&:hover': {
                backgroundColor: theme.palette.common.white,
              },
              width: 80,
              height: 80,
            }}
          >
            <PlayArrow sx={{ fontSize: 48 }} />
          </IconButton>
        </Box>
      )}

      {/* Loading Indicator */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.common.black, 0.4),
          }}
        >
          <CircularProgress color="primary" size={60} />
        </Box>
      )}
    </Box>
  );
};

export default VideoPlayer;